import { useState, useMemo, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Upload, X, FileText, Plus } from "lucide-react";
import SignaturePad from "./SignaturePad";
import ClientCodeField, { isValidClientCode, readUrlClientCode } from "./ClientCodeField";
import "./IntakeForm.css";

import { API, SENDING_ON } from "../lib/portalMode";
import PreviewNote from "./PreviewNote";
import DayFirstDate from "./DayFirstDate";
import { isBirthDate } from "../lib/dayFirst";
const CLIENT_OPTION = "I am the client, filling this in for myself";

const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;

function processHtml(html) {
  if (!html) return "";
  // make existing anchors open in a new tab
  let out = html.replace(/<a\s+/gi, '<a target="_blank" rel="noopener noreferrer" ');
  // linkify bare URLs that are not already inside an anchor tag
  const parts = out.split(/(<a[\s\S]*?<\/a>)/gi);
  out = parts
    .map((p) =>
      p.toLowerCase().startsWith("<a")
        ? p
        : p.replace(URL_RE, (u) => {
            const href = u.startsWith("http") ? u : `https://${u}`;
            return `<a href="${href}" target="_blank" rel="noopener noreferrer">${u}</a>`;
          })
    )
    .join("");
  return out;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function resolveValue(key, responses, local) {
  if (key.includes(".")) {
    const [grp, sub] = key.split(".");
    const g = responses[grp];
    return g && typeof g === "object" ? g[sub] : undefined;
  }
  if (local && key in local) return local[key];
  return responses[key];
}

function showIfMet(showIf, responses, local) {
  if (!showIf) return true;
  return Object.entries(showIf).every(([key, cond]) => {
    const val = resolveValue(key, responses, local);
    if ("equals" in cond && val !== cond.equals) return false;
    if ("not_equals" in cond && val === cond.not_equals) return false;
    if ("includes" in cond) {
      if (Array.isArray(val)) return val.includes(cond.includes);
      return val === cond.includes;
    }
    return true;
  });
}

function PlannedSupports({ planned }) {
  const rows = planned.rows || [];
  return (
    <div className="intake-planned" data-testid="planned-supports-table">
      <h4 className="intake-content-heading">Your planned supports</h4>
      {planned.agreement_period && (
        <p className="intake-planned-period"><strong>Agreement period:</strong> {planned.agreement_period}</p>
      )}
      <div className="intake-planned-scroll">
        <table className="intake-planned-table">
          <thead>
            <tr><th>Item</th><th>Description</th><th>Hours</th><th>Rate/hr</th><th>Total</th><th>Note</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.item}</td>
                <td>{r.description}</td>
                <td>{r.hours}</td>
                <td>${Number(r.rate).toFixed(2)}</td>
                <td>${Number(r.total).toFixed(2)}</td>
                <td>{r.note}</td>
              </tr>
            ))}
            <tr className="intake-planned-total">
              <td colSpan={4} style={{ textAlign: "right" }}><strong>Grand total</strong></td>
              <td><strong>${Number(planned.grand_total).toFixed(2)}</strong></td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function IntakeConsentForm({ schema, responses, setResponses, onSubmitSuccess, agreementToken, clientRef, codeRequired }) {
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Day-first dates that are half-typed or impossible (a DOB in the future, 31 April): shown under the
  // field straight away and stop the form being sent until fixed or cleared.
  const [dateProblems, setDateProblems] = useState({});
  const noteDateProblem = (key, msg) =>
    setDateProblems((prev) => {
      const next = msg || undefined;
      if (prev[key] === next) return prev;
      const out = { ...prev };
      if (next) out[key] = next; else delete out[key];
      return out;
    });
  const [referralFiles, setReferralFiles] = useState([]);
  const [planned, setPlanned] = useState(null);
  const [clientCode, setClientCode] = useState(() => readUrlClientCode());
  const [noCode, setNoCode] = useState(false);
  const [codePrefilled, setCodePrefilled] = useState(() => !!readUrlClientCode());
  const fileInputRef = useRef(null);
  const didInit = useRef(false);

  useEffect(() => {
    if (!agreementToken) return;
    axios
      .get(`${API}/forms/agreement/${agreementToken}`)
      .then((r) => setPlanned(r.data))
      .catch(() => setPlanned(null));
  }, [agreementToken]);

  // Pre-fill read-only "Date signed" fields with today + seed repeater minimums
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const updates = {};
    (schema.sections || []).forEach((s) =>
      (s.fields || []).forEach((f) => {
        if (f.type === "date" && f.read_only && f.default === "today" && !responses[f.id]) {
          updates[f.id] = todayISO();
        }
        if (f.type === "repeater" && !Array.isArray(responses[f.id])) {
          const min = f.min || 0;
          updates[f.id] = Array.from({ length: Math.max(min, 1) }, () => ({}));
        }
      })
    );
    if (Object.keys(updates).length) setResponses((prev) => ({ ...prev, ...updates }));
  }, []);

  const setVal = (id, value) => {
    setResponses((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const setSubVal = (groupId, subId, value) => {
    setResponses((prev) => ({ ...prev, [groupId]: { ...(prev[groupId] || {}), [subId]: value } }));
    setErrors((prev) => { const n = { ...prev }; delete n[`${groupId}.${subId}`]; return n; });
  };

  const toggleCheckbox = (id, option, current) => {
    const arr = Array.isArray(current) ? [...current] : [];
    const i = arr.indexOf(option);
    if (i >= 0) arr.splice(i, 1); else arr.push(option);
    setVal(id, arr);
  };

  const setRepeaterItem = (repId, index, subId, value) => {
    setResponses((prev) => {
      const items = Array.isArray(prev[repId]) ? prev[repId].map((it) => ({ ...it })) : [];
      if (!items[index]) items[index] = {};
      items[index][subId] = value;
      return { ...prev, [repId]: items };
    });
    setErrors((prev) => { const n = { ...prev }; delete n[`${repId}.${index}.${subId}`]; return n; });
  };

  const addRepeaterItem = (repId, max) => {
    setResponses((prev) => {
      const items = Array.isArray(prev[repId]) ? [...prev[repId]] : [];
      if (max && items.length >= max) return prev;
      return { ...prev, [repId]: [...items, {}] };
    });
  };

  const removeRepeaterItem = (repId, index, min) => {
    setResponses((prev) => {
      const items = Array.isArray(prev[repId]) ? [...prev[repId]] : [];
      if (items.length <= (min || 1)) return prev;
      items.splice(index, 1);
      return { ...prev, [repId]: items };
    });
  };

  const patternOk = (field, value) => {
    if (!field.pattern || !value) return true;
    try { return new RegExp(field.pattern).test(String(value)); } catch { return true; }
  };

  const patternMessage = (field) => {
    const p = field.pattern || "";
    if (p === "^\\d{4}$") return "Please enter a 4-digit postcode.";
    if (p === "^\\d{10}$") return "Please enter a 10-digit Medicare number.";
    if (p === "^\\d{9}$") return "Please enter a 9-digit NDIS number.";
    return "Please check this value.";
  };

  const validate = () => {
    const newErrors = {};
    (schema.sections || []).forEach((section) =>
      (section.fields || []).forEach((field) => {
        if (field.type === "content" || field.type === "provider_signature") return;
        if (!showIfMet(field.show_if, responses)) return;

        if (field.type === "funding_details") {
          const group = responses[field.id] || {};
          (field.subfields || []).forEach((sub) => {
            if (!showIfMet(sub.show_if, responses, group)) return;
            const v = group[sub.id];
            if (dateProblems[`${field.id}.${sub.id}`]) {
              newErrors[`${field.id}.${sub.id}`] = dateProblems[`${field.id}.${sub.id}`];
            } else if (sub.required && (v === undefined || v === "" || (Array.isArray(v) && v.length === 0))) {
              newErrors[`${field.id}.${sub.id}`] = "This field is required.";
            } else if (v && !patternOk(sub, v)) {
              newErrors[`${field.id}.${sub.id}`] = patternMessage(sub);
            }
          });
          return;
        }

        if (field.type === "repeater") {
          const items = Array.isArray(responses[field.id]) ? responses[field.id] : [];
          const min = field.min || 0;
          if (field.required && items.length < Math.max(min, 1)) {
            newErrors[field.id] = "Please add at least one.";
          }
          items.forEach((item, idx) => {
            (field.subfields || []).forEach((sub) => {
              if (!showIfMet(sub.show_if, responses, item)) return;
              const v = item[sub.id];
              if (sub.required && (v === undefined || v === "" || (Array.isArray(v) && v.length === 0))) {
                newErrors[`${field.id}.${idx}.${sub.id}`] = "This field is required.";
              }
            });
          });
          return;
        }

        const v = responses[field.id];
        if (field.type === "signature") {
          if (field.required && (!v || !String(v).startsWith("data:image"))) {
            newErrors[field.id] = "Please draw your signature.";
          }
          return;
        }
        if (dateProblems[field.id]) {
          newErrors[field.id] = dateProblems[field.id];
        } else if (field.required && (v === undefined || v === "" || v === false || (Array.isArray(v) && v.length === 0))) {
          newErrors[field.id] = "This field is required.";
        } else if (v && !patternOk(field, v)) {
          newErrors[field.id] = patternMessage(field);
        }
      })
    );
    return newErrors;
  };

  const handleSubmit = async () => {
    if (!SENDING_ON) return; // off the live portal nothing is sent (lib/portalMode.js)
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error("Please complete the highlighted fields before submitting.");
      const firstKey = Object.keys(found)[0].split(".")[0];
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="intake-field-${firstKey}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
      return;
    }
    // Client code: required when CODE_REQUIRED, otherwise optional, but if given, must be valid
    const codeBad = codeRequired ? !isValidClientCode(clientCode) : (!noCode && clientCode && !isValidClientCode(clientCode));
    if (codeBad) {
      toast.error("Please check your code: two letters then the month and year of birth, like ST0615.");
      setTimeout(() => {
        const el = document.querySelector('[data-testid="client-code-input"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      const submitPayload = { slug: schema.slug, responses };
      if (agreementToken) submitPayload.agreement_token = agreementToken;
      if (clientRef) submitPayload.ref = clientRef;
      if (clientCode) submitPayload.client_code = clientCode;
      formData.append("payload", JSON.stringify(submitPayload));
      referralFiles.forEach((f) => formData.append("referral_files", f));
      const res = await axios.post(`${API}/forms/submit`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success("Form submitted successfully.");
      onSubmitSuccess(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const providerSigUrl = useMemo(
    () => (agreementToken ? `${API}/forms/provider-signature?agreement=${encodeURIComponent(agreementToken)}` : null),
    [agreementToken]
  );

  const renderRepeaterSub = (sub, repId, index, item) => {
    if (!showIfMet(sub.show_if, responses, item)) return null;
    const errKey = `${repId}.${index}.${sub.id}`;
    const err = errors[errKey];
    const value = item[sub.id];
    const onChange = (v) => setRepeaterItem(repId, index, sub.id, v);
    const Lbl = (
      <label className="intake-field-label">
        {sub.label}
        {sub.required && <span className="intake-required-star">*</span>}
      </label>
    );
    const testid = `intake-rep-${repId}-${index}-${sub.id}`;
    let control;
    if (sub.type === "select") {
      control = (
        <select className="intake-select" aria-invalid={!!err} value={value || ""}
          onChange={(e) => onChange(e.target.value)} data-testid={testid}>
          <option value="">Please select…</option>
          {(sub.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    } else if (sub.type === "textarea") {
      control = <textarea className="intake-textarea" aria-invalid={!!err} value={value || ""}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
    } else if (sub.type === "checkboxes") {
      const arr = Array.isArray(value) ? value : [];
      control = (
        <div className="intake-options">
          {(sub.options || []).map((opt) => {
            const checked = arr.includes(opt);
            return (
              <label key={opt} className={`intake-option ${checked ? "selected" : ""}`}
                data-testid={`${testid}-${opt.slice(0, 16).replace(/\s+/g, "-")}`}>
                <input type="checkbox" checked={checked} onChange={() => {
                  const a = [...arr]; const i = a.indexOf(opt); if (i >= 0) a.splice(i, 1); else a.push(opt);
                  onChange(a);
                }} />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      );
    } else {
      control = <input type={sub.type === "email" ? "email" : "text"} className="intake-input"
        aria-invalid={!!err} value={value || ""} placeholder={sub.placeholder || ""}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
    }
    return (
      <div className="intake-field" key={sub.id}>
        {Lbl}
        {control}
        {err && <span className="intake-field-error">{err}</span>}
      </div>
    );
  };

  const renderField = (field, inGroup, groupId) => {
    const local = inGroup ? (responses[groupId] || {}) : null;
    if (!showIfMet(field.show_if, responses, local)) return null;

    const id = field.id;
    const errKey = inGroup ? `${groupId}.${id}` : id;
    const err = errors[errKey];
    const value = inGroup ? (responses[groupId]?.[id]) : responses[id];
    const label = field.label;
    const req = field.required;

    const Label = () =>
      label ? (
        <label className="intake-field-label" htmlFor={id}>
          {field.label_html ? (
            <span dangerouslySetInnerHTML={{ __html: processHtml(field.label_html) }} />
          ) : (
            label
          )}
          {req && <span className="intake-required-star">*</span>}
          {field.help_text && <span className="intake-help-text">{field.help_text}</span>}
        </label>
      ) : null;

    const onChange = (v) => (inGroup ? setSubVal(groupId, id, v) : setVal(id, v));

    switch (field.type) {
      case "content": {
        let html = field.html || "";
        const isSchedule = html.includes("<table") && (field.heading || "").includes("Schedule of Supports");
        if (planned) {
          html = html.replace(
            "Frequency, duration, and total hours for each support will be determined in consultation with you and documented in your individual support plan.",
            "The supports above are what we have planned together. If anything changes, we will agree it with you in writing first."
          );
        }
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <div className="intake-content">
              {field.heading && <h4 className="intake-content-heading">{field.heading}</h4>}
              <div dangerouslySetInnerHTML={{ __html: processHtml(html) }} />
            </div>
            {planned && isSchedule && <PlannedSupports planned={planned} />}
          </div>
        );
      }

      case "text":
      case "email":
      case "phone":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <input
              id={id}
              type={field.type === "email" ? "email" : "text"}
              inputMode={field.type === "phone" ? "tel" : undefined}
              className="intake-input"
              aria-invalid={!!err}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`intake-input-${id}`}
            />
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "textarea":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <textarea
              id={id}
              className="intake-textarea"
              aria-invalid={!!err}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`intake-input-${id}`}
            />
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "date":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <DayFirstDate
              id={id}
              label={label}
              value={value || ""}
              readOnly={!!field.read_only}
              notFuture={isBirthDate(id)}
              inputClassName="intake-input"
              ariaInvalid={!!err}
              onChange={(v) => onChange(v)}
              onProblem={(m) => noteDateProblem(errKey, m)}
              testId={`intake-input-${id}`}
            />
            {err && err !== dateProblems[errKey] && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "month_year":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <input
              id={id}
              type="month"
              className="intake-input"
              aria-invalid={!!err}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`intake-input-${id}`}
            />
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "select":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <select
              id={id}
              className="intake-select"
              aria-invalid={!!err}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`intake-input-${id}`}
            >
              <option value="">Please select…</option>
              {(field.options || []).map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "radio":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <div className="intake-options">
              {(field.options || []).map((opt) => (
                <label
                  key={opt}
                  className={`intake-option ${value === opt ? "selected" : ""}`}
                  data-testid={`intake-radio-${id}-${opt.slice(0, 20).replace(/\s+/g, "-")}`}
                >
                  <input type="radio" name={id} checked={value === opt} onChange={() => onChange(opt)} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "checkboxes":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <div className="intake-options">
              {(field.options || []).map((opt) => {
                const arr = Array.isArray(value) ? value : [];
                const checked = arr.includes(opt);
                return (
                  <label
                    key={opt}
                    className={`intake-option ${checked ? "selected" : ""}`}
                    data-testid={`intake-check-${id}-${opt.slice(0, 20).replace(/\s+/g, "-")}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => (inGroup ? setSubVal(groupId, id, (() => { const a = [...arr]; const i = a.indexOf(opt); if (i >= 0) a.splice(i, 1); else a.push(opt); return a; })()) : toggleCheckbox(id, opt, value))}
                    />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "checkbox":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <label className={`intake-consent-tick ${value ? "checked" : ""}`}>
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={`intake-input-${id}`}
              />
              <span>
                {field.label_html ? (
                  <span dangerouslySetInnerHTML={{ __html: processHtml(field.label_html) }} />
                ) : (
                  label
                )}
                {req && <span className="intake-required-star">*</span>}
              </span>
            </label>
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "funding_details":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <div className="intake-funding-group">
              {(field.subfields || []).map((sub) => (
                <div key={sub.id} className="intake-subfield">
                  {renderField(sub, true, id)}
                </div>
              ))}
            </div>
          </div>
        );

      case "file":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <div
              className="intake-file-drop"
              onClick={() => fileInputRef.current?.click()}
              data-testid={`intake-file-drop-${id}`}
            >
              <Upload size={22} style={{ margin: "0 auto 8px" }} />
              <div>Click to upload{field.multiple ? " (you can add more than one)" : ""}</div>
              <div style={{ fontSize: "0.78rem", marginTop: 4 }}>
                {(field.accept || []).join(", ")} · up to {field.max_mb || 20} MB each
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple={field.multiple}
              accept={(field.accept || []).join(",")}
              style={{ display: "none" }}
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                const max = (field.max_mb || 20) * 1024 * 1024;
                const ok = picked.filter((f) => {
                  if (f.size > max) { toast.error(`${f.name} is larger than ${field.max_mb || 20} MB.`); return false; }
                  return true;
                });
                setReferralFiles((prev) => [...prev, ...ok]);
                e.target.value = "";
              }}
              data-testid={`intake-file-input-${id}`}
            />
            {referralFiles.map((f, i) => (
              <div key={i} className="intake-file-item" data-testid={`intake-file-item-${i}`}>
                <FileText size={16} />
                <span>{f.name}</span>
                <button
                  type="button"
                  className="intake-file-remove"
                  onClick={() => setReferralFiles((prev) => prev.filter((_, j) => j !== i))}
                  data-testid={`intake-file-remove-${i}`}
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        );

      case "signature":
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            <SignaturePad value={value} onChange={onChange} testId={`intake-signature-${id}`} />
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );

      case "repeater": {
        const items = Array.isArray(responses[id]) ? responses[id] : [];
        const err = errors[id];
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <Label />
            {items.map((item, idx) => (
              <div className="intake-rep-card" key={idx} data-testid={`intake-rep-card-${id}-${idx}`}>
                <div className="intake-rep-card-head">
                  <span>{(field.item_label || "Item")} {idx + 1}</span>
                  {items.length > (field.min || 1) && (
                    <button type="button" className="intake-rep-remove"
                      onClick={() => removeRepeaterItem(id, idx, field.min || 1)}
                      data-testid={`intake-rep-remove-${id}-${idx}`}>
                      <X size={15} /> Remove
                    </button>
                  )}
                </div>
                {(field.subfields || []).map((sub) => renderRepeaterSub(sub, id, idx, item))}
              </div>
            ))}
            {(!field.max || items.length < field.max) && (
              <button type="button" className="intake-rep-add"
                onClick={() => addRepeaterItem(id, field.max)}
                data-testid={`intake-rep-add-${id}`}>
                <Plus size={16} /> {field.add_label || "Add another"}
              </button>
            )}
            {err && <span className="intake-field-error">{err}</span>}
          </div>
        );
      }

      case "validity_notice": {
        const base = responses.date_signed || todayISO();
        const d = new Date(base);
        d.setMonth(d.getMonth() + (field.months || 12));
        const until = isNaN(d) ? "" : `${d.getDate()} ${d.toLocaleString("en-AU", { month: "long" })} ${d.getFullYear()}`;
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <div className="intake-validity">This permission is valid until <strong>{until}</strong>.</div>
          </div>
        );
      }

      case "provider_signature": {
        const signDate = responses.date_signed || todayISO();
        const [y, m, d] = String(signDate).split("-");
        const displayDate = y && m && d ? `${d}/${m}/${y}` : signDate;
        return (
          <div className="intake-field" data-testid={`intake-field-${id}`}>
            <div className="intake-provider-block">
              {providerSigUrl ? (
                <img src={providerSigUrl} alt="Provider signature" className="intake-provider-img" data-testid="provider-signature-img" />
              ) : null}
              <div className="intake-provider-name">
                Lachlan Beaton, Clinical Psychologist Registrar, AHPRA PSY0002785698
              </div>
              <div className="intake-provider-date">Date: {displayDate}</div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="intake-form" data-testid={`intake-form-${schema.slug}`}>
      <div className="intake-section" data-testid="intake-client-code-section">
        <ClientCodeField
          code={clientCode}
          setCode={setClientCode}
          noCode={noCode}
          setNoCode={setNoCode}
          codeRequired={codeRequired}
          prefilled={codePrefilled}
          onChange={() => setCodePrefilled(false)}
        />
      </div>
      {(schema.sections || []).map((section) => {
        const visibleFields = (section.fields || []).filter((f) =>
          f.type === "content" || f.type === "provider_signature" || showIfMet(f.show_if, responses)
        );
        if (visibleFields.length === 0) return null;
        return (
          <div key={section.id} className="intake-section" data-testid={`intake-section-${section.id}`}>
            {section.title && <h3 className="intake-section-title">{section.title}</h3>}
            {section.fields.map((field) => (
              <div key={field.id}>{renderField(field, false, null)}</div>
            ))}
          </div>
        );
      })}

      <div className="intake-submit-bar">
        <Button
          onClick={handleSubmit}
          disabled={submitting || !SENDING_ON}
          className="submit-button"
          data-testid="intake-submit-button"
          style={{ backgroundColor: "#2060E8", color: "#fff" }}
        >
          {submitting ? (
            <><Loader2 className="button-icon spin" style={{ marginRight: 8 }} size={16} /> Submitting…</>
          ) : (
            <><CheckCircle2 className="button-icon" style={{ marginRight: 8 }} size={16} /> Submit form</>
          )}
        </Button>
        <PreviewNote inline />
      </div>
    </div>
  );
}
